import { Ban, Check, CheckCircle2, ExternalLink, HelpCircle, Link as LinkIcon, MapPin, Plus, RotateCcw, Search, Sparkles, Store, X } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import BONavigation from "../../../components/admin/BONavigation";
import {
    getFranchiseReviewEvents,
    getFranchiseReviewCounts,
    approveFranchiseReview,
    rejectFranchiseReview,
    markFranchiseReviewUnsure,
    reopenFranchiseReview,
    FranchiseReviewItem,
    FranchiseReviewBucket,
    FranchiseReviewCounts,
} from "../../../interfaces/events";
import { getFranchises, createFranchise, FranchiseDoc } from "../../../interfaces/establishments";

function InlineFranchisePicker({
    franchises,
    selected,
    onSelect,
    onCreated,
    defaultQuery,
}: {
    franchises: FranchiseDoc[];
    selected: FranchiseDoc | null;
    onSelect: (f: FranchiseDoc | null) => void;
    onCreated: (f: FranchiseDoc) => void;
    defaultQuery?: string;
}) {
    const [query, setQuery] = useState(defaultQuery ?? "");
    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const filtered = query.length > 0
        ? franchises.filter(f => f.name.toLowerCase().includes(query.toLowerCase())).slice(0, 8)
        : franchises.slice(0, 8);

    const trimmed = query.trim();
    const exactMatch = franchises.some(f => f.name.toLowerCase() === trimmed.toLowerCase());
    const showCreate = trimmed.length >= 2 && !exactMatch;

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleCreate = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (!trimmed || creating) return;
        setCreating(true);
        try {
            const newFranchise = await createFranchise(trimmed);
            onCreated(newFranchise);
            onSelect(newFranchise);
            setQuery("");
            setOpen(false);
        } finally {
            setCreating(false);
        }
    };

    return (
        <div ref={containerRef} className="relative w-48">
            {selected ? (
                <div className="flex items-center gap-1.5 border border-orange-200 rounded-sm px-2 py-1.5 bg-orange-50">
                    <Store className="w-3 h-3 text-orange-500 shrink-0" />
                    <span className="text-[11px] font-medium text-orange-800 flex-1 truncate">{selected.name}</span>
                    <button type="button" onClick={() => { onSelect(null); setQuery(""); }} className="hover:opacity-70">
                        <X className="w-3 h-3 text-orange-600" />
                    </button>
                </div>
            ) : (
                <div
                    className="flex items-center gap-1.5 border border-gray-200 rounded-sm px-2 py-1.5 bg-white cursor-text focus-within:border-gray-400"
                    onClick={() => { inputRef.current?.focus(); setOpen(true); }}
                >
                    <Search className="w-3 h-3 text-gray-400 shrink-0" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={e => { setQuery(e.target.value); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        placeholder="Choisir ou créer..."
                        className="flex-1 border-0 outline-none focus:ring-0 text-[11px] text-gray-700 placeholder:text-gray-400 bg-transparent w-0 min-w-0"
                    />
                </div>
            )}

            {open && !selected && (
                <div className="absolute z-40 mt-1 w-56 bg-white border border-gray-200 rounded-sm shadow-sm max-h-52 overflow-y-auto left-0">
                    {filtered.length === 0 && !showCreate && (
                        <p className="px-3 py-2 text-[11px] text-gray-400">Aucun résultat</p>
                    )}
                    {filtered.map(f => (
                        <button
                            key={f._id}
                            type="button"
                            onMouseDown={e => { e.preventDefault(); onSelect(f); setQuery(""); setOpen(false); }}
                            className="w-full text-left px-3 py-1.5 text-[11px] text-gray-700 hover:bg-[#F9F6F3] flex items-center gap-2"
                        >
                            <Store className="w-3 h-3 text-gray-400 shrink-0" />
                            {f.name}
                        </button>
                    ))}
                    {showCreate && (
                        <button
                            type="button"
                            onMouseDown={handleCreate}
                            disabled={creating}
                            className="w-full text-left px-3 py-1.5 text-[11px] font-medium text-gray-900 hover:bg-[#F9F6F3] flex items-center gap-2 border-t border-gray-100 disabled:opacity-50"
                        >
                            {creating
                                ? <span className="w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin shrink-0" />
                                : <Plus className="w-3 h-3 text-gray-500 shrink-0" />
                            }
                            Créer «{trimmed}»
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function EventIdentity({ item }: { item: FranchiseReviewItem }) {
    const location = [item.establishment?.postcode, item.establishment?.city].filter(Boolean).join(" ");
    const nameDiffers =
        item.establishment && item.establishment.name.trim().toLowerCase() !== item.companyName.trim().toLowerCase();

    return (
        <div className="flex-1 min-w-0">
            <div className="flex items-start gap-1.5">
                <Store className="w-3 h-3 text-gray-400 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide shrink-0">Commercial</span>
                        <p className="text-xs font-semibold text-gray-900 truncate">
                            {item.establishment ? item.establishment.name : item.companyName}
                        </p>
                    </div>
                    {item.establishment?.legal_name &&
                        item.establishment.legal_name.trim().toLowerCase() !== item.establishment.name.trim().toLowerCase() && (
                            <div className="flex items-baseline gap-1.5 mt-0.5">
                                <span className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide shrink-0">Société</span>
                                <p className="text-[11px] text-gray-500 truncate">{item.establishment.legal_name}</p>
                            </div>
                        )}
                </div>
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap pl-[18px]">
                {location && (
                    <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                        <MapPin className="w-2.5 h-2.5" />{location}
                    </span>
                )}
                {item.siren && (
                    <a
                        href={`https://www.pappers.fr/entreprise/${item.siren}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Voir sur Pappers (nouvel onglet)"
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-gray-200 bg-white text-[11px] text-gray-600 font-mono hover:border-gray-300 hover:text-gray-900 hover:bg-[#F9F6F3] transition-colors"
                    >
                        {item.siren}
                        <ExternalLink className="w-3 h-3 text-gray-400" />
                    </a>
                )}
            </div>
            {nameDiffers && (
                <p className="text-[10px] text-gray-400 italic mt-0.5 pl-[18px] truncate">
                    Détecté sous « {item.companyName} »
                </p>
            )}
        </div>
    );
}

function DetectionBadges({ item }: { item: FranchiseReviewItem }) {
    const currentMatchesDetection =
        !!item.establishment?.franchise &&
        !!item.franchiseName &&
        item.establishment.franchise.name.toLowerCase() === item.franchiseName.trim().toLowerCase();

    return (
        <div className="w-56 shrink-0 hidden md:flex flex-col gap-1">
            {item.franchiseName && (
                <span
                    className="inline-flex items-center gap-1 w-fit max-w-full px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200"
                    title="Nom de franchise détecté automatiquement par l'IA"
                >
                    <Sparkles className="w-2.5 h-2.5 shrink-0" />
                    <span className="truncate">Détecté : {item.franchiseName}</span>
                </span>
            )}
            {item.tentativeFranchise && (
                <span
                    className="inline-flex items-center gap-1 w-fit max-w-full px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-gray-100 text-gray-600"
                    title="Franchise vers laquelle l'admin penchait en marquant « Pas sûr »"
                >
                    <HelpCircle className="w-2.5 h-2.5 shrink-0" />
                    <span className="truncate">Piste : {item.tentativeFranchise.name}</span>
                </span>
            )}
            {item.establishment?.franchise ? (
                <span
                    className="inline-flex items-center gap-1 w-fit max-w-full px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-gray-100 text-gray-600"
                    title="Franchise enregistrée en base pour cet établissement"
                >
                    <LinkIcon className="w-2.5 h-2.5 shrink-0" />
                    <span className="truncate">Actuel : {item.establishment.franchise.name}</span>
                    {currentMatchesDetection && <Check className="w-2.5 h-2.5 text-green-600 shrink-0" />}
                </span>
            ) : item.establishment ? (
                <span className="text-[10px] text-gray-300">Aucune franchise en base</span>
            ) : null}
        </div>
    );
}

function EventRow({
    item,
    bucket,
    franchises,
    onDone,
    onFranchiseCreated,
}: {
    item: FranchiseReviewItem;
    bucket: FranchiseReviewBucket;
    franchises: FranchiseDoc[];
    onDone: (eventId: string) => void;
    onFranchiseCreated: (f: FranchiseDoc) => void;
}) {
    // Pre-select: the tentative pick (unsure folder) first, else an exact name match on the
    // detected franchise — so "Valider" is a single click when there is one obvious choice.
    const exactMatch =
        (item.franchiseName &&
            franchises.find(f => f.name.toLowerCase() === item.franchiseName!.trim().toLowerCase())) ||
        null;
    const tentativeDoc =
        (item.tentativeFranchise && franchises.find(f => f._id === item.tentativeFranchise!._id)) || null;

    const [selected, setSelected] = useState<FranchiseDoc | null>(tentativeDoc ?? exactMatch);
    const [busy, setBusy] = useState<null | "approve" | "unsure" | "reject" | "reopen">(null);

    // `franchises` loads after the first render, so the pre-selection above can miss.
    // Apply it once when the match resolves — but never fight a manual clear.
    const didAutoSelect = useRef(false);
    useEffect(() => {
        if (didAutoSelect.current || selected) return;
        const auto = tentativeDoc ?? exactMatch;
        if (auto) {
            setSelected(auto);
            didAutoSelect.current = true;
        }
    }, [selected, tentativeDoc, exactMatch]);

    const run = async (kind: "approve" | "unsure" | "reject" | "reopen", fn: () => Promise<void>) => {
        setBusy(kind);
        try {
            await fn();
            onDone(item.eventId);
        } catch (err) {
            console.error(`Franchise review ${kind} failed`, err);
            setBusy(null);
        }
    };

    const spinner = <span className="w-3 h-3 border-2 border-current border-r-transparent rounded-full animate-spin opacity-70" />;

    // Read-only folders: approved / rejected
    if (bucket === "approved" || bucket === "rejected") {
        return (
            <div className="flex items-center gap-3 px-4 py-3 hover:bg-[#F9F6F3] border-b border-gray-100 last:border-0">
                <EventIdentity item={item} />
                <div className="w-56 shrink-0 hidden md:block">
                    {bucket === "approved" ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-green-50 text-green-700 border border-green-200">
                            <CheckCircle2 className="w-2.5 h-2.5" />
                            {item.establishment?.franchise?.name ?? item.franchiseName ?? "Franchise validée"}
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-gray-100 text-gray-500">
                            <Ban className="w-2.5 h-2.5" />
                            Aucune correspondance{item.franchiseName ? ` · détecté « ${item.franchiseName} »` : ""}
                        </span>
                    )}
                </div>
                <button
                    onClick={() => run("reopen", () => reopenFranchiseReview(item.eventId))}
                    disabled={busy !== null}
                    title={
                        bucket === "approved"
                            ? "Remettre à traiter — l'établissement garde sa franchise actuelle"
                            : "Remettre à traiter"
                    }
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-sm text-[11px] font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors disabled:opacity-40 shrink-0"
                >
                    {busy === "reopen" ? spinner : <RotateCcw className="w-3.5 h-3.5" />}
                    Remettre à traiter
                </button>
            </div>
        );
    }

    // Actionable folders: pending / unsure
    return (
        <div className="flex items-center gap-3 px-4 py-3 hover:bg-[#F9F6F3] border-b border-gray-100 last:border-0">
            <EventIdentity item={item} />
            <DetectionBadges item={item} />

            <div className="flex items-center gap-2 shrink-0">
                <div className="flex flex-col items-start gap-1">
                    <InlineFranchisePicker
                        franchises={franchises}
                        selected={selected}
                        onSelect={setSelected}
                        onCreated={onFranchiseCreated}
                        defaultQuery={item.franchiseName ?? ""}
                    />
                    {exactMatch && !selected && (
                        <button
                            type="button"
                            onClick={() => setSelected(exactMatch)}
                            className="flex items-center gap-0.5 text-[10px] font-medium text-gray-500 hover:text-gray-800"
                        >
                            <Check className="w-2.5 h-2.5" />
                            Confirmer « {exactMatch.name} »
                        </button>
                    )}
                </div>

                <button
                    onClick={() => selected && run("approve", () => approveFranchiseReview(item.eventId, selected._id))}
                    disabled={!selected || busy !== null}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-sm text-[11px] font-semibold transition-all ${
                        selected ? "bg-black text-white hover:bg-gray-800" : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                >
                    {busy === "approve" ? spinner : <Check className="w-3 h-3" />}
                    Valider
                </button>

                {bucket === "pending" && (
                    <button
                        onClick={() => run("unsure", () => markFranchiseReviewUnsure(item.eventId, selected?._id))}
                        disabled={busy !== null}
                        title="Pas sûr — ranger dans le dossier « Pas sûr » pour y revenir"
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-sm text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-40"
                    >
                        {busy === "unsure" ? spinner : <HelpCircle className="w-3.5 h-3.5" />}
                        Pas sûr
                    </button>
                )}

                {bucket === "unsure" && (
                    <button
                        onClick={() => run("reopen", () => reopenFranchiseReview(item.eventId))}
                        disabled={busy !== null}
                        title="Remettre dans « À traiter »"
                        className="flex items-center gap-1 px-2 py-1.5 rounded-sm text-[11px] text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-40"
                    >
                        {busy === "reopen" ? spinner : <RotateCcw className="w-3.5 h-3.5" />}
                    </button>
                )}

                <button
                    onClick={() => run("reject", () => rejectFranchiseReview(item.eventId))}
                    disabled={busy !== null}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-sm text-[11px] font-medium text-gray-500 border border-gray-200 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors disabled:opacity-40"
                    title="Aucune franchise ne correspond — ranger dans « Sans correspondance »"
                >
                    {busy === "reject" ? spinner : <X className="w-3.5 h-3.5" />}
                    Pas de correspondance
                </button>
            </div>
        </div>
    );
}

const BUCKETS: { value: FranchiseReviewBucket; label: string }[] = [
    { value: "pending", label: "À traiter" },
    { value: "unsure", label: "Pas sûr" },
    { value: "approved", label: "Validés" },
    { value: "rejected", label: "Sans correspondance" },
];

const STATS_NOUN: Record<FranchiseReviewBucket, string> = {
    pending: "à traiter",
    unsure: "en attente d'un second avis",
    approved: "validé·s",
    rejected: "sans correspondance",
};

const EMPTY_COPY: Record<FranchiseReviewBucket, { title: string; hint: string }> = {
    pending: { title: "Aucun événement à traiter", hint: "Toutes les détections ont été triées" },
    unsure: { title: "Rien en « Pas sûr »", hint: "Les événements marqués « Pas sûr » atterrissent ici" },
    approved: { title: "Aucune franchise validée", hint: "Les validations apparaissent ici" },
    rejected: { title: "Aucun « sans correspondance »", hint: "Les événements sans franchise correspondante atterrissent ici" },
};

export default function FranchiseEventReview() {
    const [bucket, setBucket] = useState<FranchiseReviewBucket>("pending");
    const [counts, setCounts] = useState<FranchiseReviewCounts | null>(null);
    const [items, setItems] = useState<FranchiseReviewItem[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [franchises, setFranchises] = useState<FranchiseDoc[]>([]);

    const LIMIT = 25;

    const refreshCounts = useCallback(() => {
        getFranchiseReviewCounts().then(setCounts).catch(console.error);
    }, []);

    // « À traiter » ne liste que les établissements sans franchise attribuée (hasFranchise: "none").
    const fetchItems = useCallback(async (pg: number, bkt: FranchiseReviewBucket) => {
        setLoading(true);
        try {
            const data = await getFranchiseReviewEvents({ page: pg, limit: LIMIT, hasFranchise: "none", status: bkt });
            setItems(data.data);
            setTotal(data.total);
            setTotalPages(data.totalPages);
        } catch (err) {
            console.error("Failed to fetch franchise review events", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchItems(1, bucket);
        refreshCounts();
        getFranchises().then(setFranchises).catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchItems]);

    const handleBucketChange = (next: FranchiseReviewBucket) => {
        if (next === bucket) return;
        setBucket(next);
        setPage(1);
        fetchItems(1, next);
    };

    const handlePageChange = (pg: number) => {
        setPage(pg);
        fetchItems(pg, bucket);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const removeItem = (eventId: string) => {
        setItems(prev => prev.filter(i => i.eventId !== eventId));
        setTotal(prev => Math.max(0, prev - 1));
        refreshCounts();
    };

    const empty = EMPTY_COPY[bucket];

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                <div className="mb-6">
                    <h1 className="text-xl font-semibold text-black">Franchise Review</h1>
                    <p className="text-xs text-gray-600 mt-0.5">
                        Événements où une franchise a été détectée automatiquement — à valider, marquer « Pas sûr » ou « Pas de correspondance »
                    </p>
                </div>

                <BONavigation />

                {/* Folder tabs */}
                <nav className="flex gap-6 border-b border-gray-200 mb-4">
                    {BUCKETS.map(b => {
                        const active = bucket === b.value;
                        const n = counts?.[b.value];
                        const badgeTone = active
                            ? "bg-black text-white"
                            : b.value === "pending" && n
                            ? "bg-red-500 text-white"
                            : b.value === "unsure" && n
                            ? "bg-amber-500 text-white"
                            : "bg-gray-100 text-gray-500";
                        return (
                            <button
                                key={b.value}
                                onClick={() => handleBucketChange(b.value)}
                                className={`relative -mb-px py-2.5 text-sm font-medium border-b-2 flex items-center gap-1.5 ${
                                    active
                                        ? "border-black text-black"
                                        : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300"
                                }`}
                            >
                                {b.label}
                                {n !== undefined && (
                                    <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${badgeTone}`}>
                                        {n > 999 ? "999+" : n}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </nav>

                {/* Stats bar */}
                <div className="mb-4 flex items-center gap-4 min-h-[28px]">
                    {!loading && (
                        <span className="text-xs text-gray-500">
                            {total.toLocaleString("fr-FR")} événement{total > 1 ? "s" : ""} {STATS_NOUN[bucket]}
                            {bucket === "pending" && " · établissements sans franchise attribuée"}
                        </span>
                    )}
                </div>

                {/* Table */}
                <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 border-b border-gray-200">
                        <span className="flex-1 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                            {loading ? "Chargement..." : "Établissement"}
                        </span>
                        <span className="w-56 text-[11px] font-semibold text-gray-500 uppercase tracking-wide hidden md:block">
                            {bucket === "approved" ? "Franchise validée" : bucket === "rejected" ? "Décision" : "Détecté (IA) vs actuel"}
                        </span>
                        <span className="w-72 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                            {bucket === "approved" || bucket === "rejected" ? "" : "Assigner une franchise"}
                        </span>
                    </div>

                    {loading ? (
                        <div className="divide-y divide-gray-100">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
                                    <div className="flex-1 space-y-1.5">
                                        <div className="h-3.5 bg-gray-100 rounded w-1/2" />
                                        <div className="h-2.5 bg-gray-100 rounded w-1/4" />
                                    </div>
                                    <div className="w-56 h-8 bg-gray-100 rounded hidden md:block" />
                                    <div className="w-72 h-8 bg-gray-100 rounded" />
                                </div>
                            ))}
                        </div>
                    ) : items.length === 0 ? (
                        <div className="py-20 text-center">
                            <Store className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                            <p className="text-sm text-gray-400">{empty.title}</p>
                            <p className="text-xs text-gray-300 mt-1">{empty.hint}</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {items.map(item => (
                                <EventRow
                                    key={item.eventId}
                                    item={item}
                                    bucket={bucket}
                                    franchises={franchises}
                                    onDone={removeItem}
                                    onFranchiseCreated={f => setFranchises(prev => [...prev, f].sort((a, b) => a.name.localeCompare(b.name, "fr")))}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-4">
                        <button
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page <= 1}
                            className="px-3 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Précédent
                        </button>
                        <span className="text-xs text-gray-500">Page {page} / {totalPages}</span>
                        <button
                            onClick={() => handlePageChange(page + 1)}
                            disabled={page >= totalPages}
                            className="px-3 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Suivant
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
